import { type Coords } from '#shared/lib/coords';
import { Map as OlMap, View } from 'ol';
import { apply } from 'ol-mapbox-style';
import { fromCoords, toCoords } from './utils';
import { transformExtent } from 'ol/proj';

export type MapOptions = {
  center?: Coords;
  staticUrl?: string;
};

export class Map {
  static defaultCenter: Coords = [55.76, 37.64]; // The center of Moscow

  readonly olMap: OlMap;

  constructor(element: HTMLElement, options: MapOptions = {}) {
    const map = new OlMap({
      target: element,
      controls: [],
      pixelRatio: window.devicePixelRatio * 1.5,
      view: new View({
        center: options.center ?? fromCoords(Map.defaultCenter),
        zoom: 14,
      }),
    });
    this.olMap = map;
    const staticUrl = options.staticUrl ?? '';
    apply(map, `${staticUrl}/styles/hot-osm.json`);
  }

  changeZoom(delta: 1 | -1, duration = 250) {
    const zoom = this.olMap.getView()?.getZoom() ?? 0;
    this.olMap.getView().animate({ duration, zoom: zoom + delta });
  }

  get center() {
    return toCoords(this.olMap.getView().getCenter()!);
  }

  set center(center) {
    this.olMap.getView().setCenter(fromCoords(center));
  }

  get extent() {
    const extent = this.olMap.getView().calculateExtent(this.olMap.getSize());
    return transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
  }

  set extent(extent) {
    extent = transformExtent(extent, 'EPSG:4326', 'EPSG:3857');
    this.olMap.getView().fit(extent);
  }
}
